import { memo, useEffect, useState } from 'react';
import { isNil, values } from 'lodash';
import { useLocation } from 'react-router-dom';
import { RoutesList } from 'routes';
import PropTypes from 'prop-types';
// Import Components
import { CampaignActionsModals } from 'components/CampaignList';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
import useToaster from 'hooks/UseToaster';
// Import Config
import { modalsStateSetDictKeys } from 'pages/CampaignListPage/config';
// Import Components
import { TemplateActionsModals } from 'components/TemplateList';
import { SegmentActionsModals } from 'components/SegmentList';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Constants
import { ModalsActions, SegmentTypesLabels, TemplateTypesLabels } from 'constants/common';

const {
    TEMPLATE_EDIT_EMAIL,
    TEMPLATE_EDIT_SMS,
    TEMPLATE_EDIT_INTERNAL_MESSAGE,
    TEMPLATE_EDIT_SOCIAL_TELEGRAM,
    TEMPLATE_EDIT_PUSH_NOTIFICATION,
    TEMPLATE_EDIT_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_DYNAMIC_POP_UP,
    TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_CMS_BANNER,
    TEMPLATE_EDIT_SOCIAL_VIBER,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP_PROVIDER,
    SEGMENT_DYNAMIC_EDIT,
    SEGMENT_COMPOSITE_EDIT,
} = RoutesList;

const StateFullCampaignActionsModals = ({ campaignId, handleClose }) => {
    const { search } = useLocation();
    const { goToRoute } = useGoToRoute();
    const { showToaster } = useToaster();
    const { doPostRequest } = useRequest();
    const goToRouteQueryParams = useQueryParams();

    const { getMessageProvidersRequest } = useCancelableRequests({
        getMessageProvidersRequest: UtilsHttpService.getPartnerMessageProviders,
    });

    const [templateProviders, setTemplateProviders] = useState({});
    // Campaign Tab Action Modals States
    const [isInfoModalOpenedState, setIsInfoModalOpenedState] = useState(true);
    const [isDeleteModalOpenedState, setIsDeleteModalOpenedState] = useState(false);
    const [isArchiveModalOpenedState, setIsArchiveModalOpenedState] = useState(false);
    const [isUnArchiveModalOpenedState, setIsUnArchiveModalOpenedState] = useState(false);
    const [isStopSendModalOpenedState, setIsStopSendModalOpenedState] = useState(false);
    const [isAddGoalModalOpenedState, setIsAddGoalModalOpenedState] = useState(false);
    const [isDeleteGoalModalOpenedState, setIsDeleteGoalModalOpenedState] = useState(false);
    const [isStopABTestModalOpenedState, setIsStopABTestModalOpenedState] = useState(false);
    const [openedModalData, setOpenedModalData] = useState<AnyObject>({ CampaignId: campaignId });

    // Template Tab Action Modals States
    const [isTemplateDeleteModalOpenedState, setIsTemplateDeleteModalOpenedState] = useState(false);
    const [isTestTemplateModalOpenedState, setIsTestTemplateModalOpenedState] = useState(false);
    const [isTemplateArchiveModalOpenedState, setIsTemplateArchiveModalOpenedState] = useState(false);
    const [isTemplateUnArchiveModalOpenedState, setIsTemplateUnArchiveModalOpenedState] = useState(false);
    const [isTemplateUsedInConfirmationModalOpenedState, setIsTemplateUsedInConfirmationModalOpenedState] =
        useState(false);

    // Segment Tab Action Modals States
    const [isSegmentDeleteModalOpenedState, setIsSegmentDeleteModalOpenedState] = useState(false);
    const [isConvertToReportModalOpenedState, setIsConvertToReportModalOpenedState] = useState(false);
    const [isSegmentArchiveModalOpenedState, setIsSegmentArchiveModalOpenedState] = useState(false);
    const [isSegmentUnArchiveModalOpenedState, setIsSegmentUnArchiveModalOpenedState] = useState(false);
    const [isSegmentUsedInConfirmationModalOpenedState, setIsSegmentUsedInConfirmationModalOpenedState] =
        useState(false);

    useEffect(() => {
        if (isInfoModalOpenedState) {
            getTemplateProviders(TemplateTypesLabels.SMS);
            getTemplateProviders(TemplateTypesLabels.Email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInfoModalOpenedState]);

    const getTemplateProviders = (channel) => {
        doPostRequest(getMessageProvidersRequest.request, {
            requestBody: { DeliveryMethod: channel },
            successCallback: (Data) => {
                setTemplateProviders((prev) => {
                    prev[channel] = (isNil(Data) ? [] : Data).reduce((acc, item) => {
                        acc[item.Id] = { label: item.Name, value: item.Id };
                        return acc;
                    }, {});
                    return { ...prev };
                });
            },
        });
    };

    const modalsStateSetDict = {
        [modalsStateSetDictKeys.deleteCampaign]: {
            key: 'deleteCampaign',
            fn: setIsDeleteModalOpenedState,
        },
        [modalsStateSetDictKeys.archiveCampaign]: {
            key: 'archiveCampaign',
            fn: setIsArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.unarchiveCampaign]: {
            key: 'unarchiveCampaign',
            fn: setIsUnArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.stopSendCampaign]: {
            key: 'stopSendCampaign',
            fn: setIsStopSendModalOpenedState,
        },
        [modalsStateSetDictKeys.infoCampaign]: {
            key: 'infoCampaign',
            fn: setIsInfoModalOpenedState,
        },
        [modalsStateSetDictKeys.setGoalCampaign]: {
            key: 'setGoalCampaign',
            fn: setIsAddGoalModalOpenedState,
        },
        [modalsStateSetDictKeys.deleteGoalCampaign]: {
            key: 'deleteGoalCampaign',
            fn: setIsDeleteGoalModalOpenedState,
        },
        [modalsStateSetDictKeys.deleteTemplate]: {
            key: 'deleteTemplate',
            fn: setIsTemplateDeleteModalOpenedState,
        },
        [modalsStateSetDictKeys.testTemplate]: {
            key: 'testTemplate',
            fn: setIsTestTemplateModalOpenedState,
        },
        [modalsStateSetDictKeys.archiveTemplate]: {
            key: 'archiveTemplate',
            fn: setIsTemplateArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.unarchiveTemplate]: {
            key: 'unarchiveTemplate',
            fn: setIsTemplateUnArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.editTemplate]: {
            key: 'editTemplate',
            fn: setIsTemplateUsedInConfirmationModalOpenedState,
        },

        [modalsStateSetDictKeys.deleteSegment]: {
            key: 'deleteSegment',
            fn: setIsSegmentDeleteModalOpenedState,
        },
        [modalsStateSetDictKeys.convertToReport]: {
            key: 'convertToReport',
            fn: setIsConvertToReportModalOpenedState,
        },
        [modalsStateSetDictKeys.archiveSegment]: {
            key: 'archiveSegment',
            fn: setIsSegmentArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.unarchiveSegment]: {
            key: 'unarchiveSegment',
            fn: setIsSegmentUnArchiveModalOpenedState,
        },

        [modalsStateSetDictKeys.editSegment]: {
            key: 'editSegment',
            fn: setIsSegmentUsedInConfirmationModalOpenedState,
        },
        [modalsStateSetDictKeys.stopABTest]: {
            key: 'stopABTest',
            fn: setIsStopABTestModalOpenedState,
        },
    };

    const onCloseModalHandler = (modalStateKey, action, alertType, alertMessage) => {
        if (
            [
                ModalsActions.DELETE,
                ModalsActions.ARCHIVE,
                ModalsActions.UNARCHIVE,
                ModalsActions.STOP,
                ModalsActions.SET_GOAL,
                ModalsActions.DELETE_GOAL,
                ModalsActions.CONVERT_TO_REPORT,
                ModalsActions.TEST_TEMPLATE,
                ModalsActions.STOP_AB_TEST,
            ].includes(action)
        ) {
            showToaster(alertType, alertMessage);
        }
        modalsStateSetDict[modalStateKey].fn(false);
        handleClose();
    };

    const handleInfoModalActionClick = (modalKey, data) => {
        setIsInfoModalOpenedState(false);
        if (modalKey) {
            setOpenedModalData(data);
            modalsStateSetDict[modalKey].fn(true);
        } else {
            handleClose();
        }
    };

    // Segment Actions Part
    const goToEditSegment = (segment) => {
        const { Type, SegmentId } = segment;
        const params = { segmentId: SegmentId };
        const path = SegmentTypesLabels.Dynamic === Type ? SEGMENT_DYNAMIC_EDIT : SEGMENT_COMPOSITE_EDIT;

        goToRoute(path, params, goToRouteQueryParams.encode({ backParams: search }));
        handleClose();
    };

    // Template Actions Part
    const goToEditTemplate = (template) => {
        const { DeliveryMethod, TemplateId } = template;
        const params = { templateId: TemplateId };
        let path;

        switch (DeliveryMethod) {
            case TemplateTypesLabels.Email:
                path = TEMPLATE_EDIT_EMAIL;
                break;
            case TemplateTypesLabels.SMS:
                path = TEMPLATE_EDIT_SMS;
                break;
            case TemplateTypesLabels.Internal:
                path = TEMPLATE_EDIT_INTERNAL_MESSAGE;
                break;
            case TemplateTypesLabels.Telegram:
                path = TEMPLATE_EDIT_SOCIAL_TELEGRAM;
                break;
            case TemplateTypesLabels.PushNotification:
                path = TEMPLATE_EDIT_PUSH_NOTIFICATION;
                break;
            case TemplateTypesLabels.PopUp:
                path = TEMPLATE_EDIT_POP_UP_NOTIFICATION;
                break;
            case TemplateTypesLabels.DynamicPopUp:
                path = TEMPLATE_EDIT_DYNAMIC_POP_UP;
                break;
            case TemplateTypesLabels.CMSPopUp:
                path = TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION;
                break;
            case TemplateTypesLabels.CMSBanner:
                path = TEMPLATE_EDIT_CMS_BANNER;
                break;
            case TemplateTypesLabels.Viber:
                path = TEMPLATE_EDIT_SOCIAL_VIBER;
                break;
            case TemplateTypesLabels.WhatsApp:
                path = TEMPLATE_EDIT_SOCIAL_WHATS_APP;
                break;
            case TemplateTypesLabels.WhatsAppProvider:
                path = TEMPLATE_EDIT_SOCIAL_WHATS_APP_PROVIDER;
                break;
            default:
        }

        goToRoute(path, params, goToRouteQueryParams.encode({ backParams: search }));
        handleClose();
    };

    return (
        <div>
            <CampaignActionsModals
                isDeleteModalOpenedState={isDeleteModalOpenedState}
                isArchiveModalOpenedState={isArchiveModalOpenedState}
                isUnArchiveModalOpenedState={isUnArchiveModalOpenedState}
                isStopSendModalOpenedState={isStopSendModalOpenedState}
                isInfoModalOpenedState={isInfoModalOpenedState}
                isAddGoalModalOpenedState={isAddGoalModalOpenedState}
                isDeleteGoalModalOpenedState={isDeleteGoalModalOpenedState}
                isStopABTestModalOpenedState={isStopABTestModalOpenedState}
                onCloseModalHandler={onCloseModalHandler}
                openedModalData={openedModalData}
                handleInfoModalActionClick={handleInfoModalActionClick}
                templateProviders={templateProviders}
            />
            <TemplateActionsModals
                isDeleteModalOpenedState={isTemplateDeleteModalOpenedState}
                isArchiveModalOpenedState={isTemplateArchiveModalOpenedState}
                isUnArchiveModalOpenedState={isTemplateUnArchiveModalOpenedState}
                isUsedInConfirmationModalOpenedState={isTemplateUsedInConfirmationModalOpenedState}
                isTestTemplateModalOpenedState={isTestTemplateModalOpenedState}
                onCloseModalHandler={onCloseModalHandler}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={openedModalData}
                goToEditTemplate={goToEditTemplate}
                providers={values(templateProviders[openedModalData.DeliveryMethod])}
            />
            <SegmentActionsModals
                isInfoModalOpenedState={false}
                isDeleteModalOpenedState={isSegmentDeleteModalOpenedState}
                isConvertToReportModalOpenedState={isConvertToReportModalOpenedState}
                isArchiveModalOpenedState={isSegmentArchiveModalOpenedState}
                isUnArchiveModalOpenedState={isSegmentUnArchiveModalOpenedState}
                isUsedInConfirmationModalOpenedState={isSegmentUsedInConfirmationModalOpenedState}
                onCloseModalHandler={onCloseModalHandler}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={openedModalData}
                goToEditSegment={goToEditSegment}
            />
        </div>
    );
};

StateFullCampaignActionsModals.propTypes = {
    campaignId: PropTypes.string.isRequired,
    handleClose: PropTypes.func.isRequired,
};

export default memo(StateFullCampaignActionsModals);
